import { Box, ClickAwayListener, InputAdornment, Tooltip, Typography, useTheme } from '@mui/material';
import Button from 'components/Common/Button';
import Input from 'components/Common/Input';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import Grid from '@mui/material/Grid2';
import { useGetAgent, useUpdateAgentConfig } from 'apis/business/agent/hook';

import { Link } from 'react-router-dom';
import * as urls from 'routes/config';
import { WEB_EMBED_SCRIPT } from 'env';
import BrushIcon from 'assets/icons/BrushIcon';
import EyeIcon from 'assets/icons/EyeIcon';
import DeployWebLoader from './components/Loader';
import EmbedWebCenterIcon from '@/assets/icons/EmbedWebCenterIcon';
import EmbedWebRightIcon from '@/assets/icons/EmbedWebRightIcon';
import EmbedWebLeftIcon from '@/assets/icons/EmbedWebLeftIcon';

export default function DeployWeb() {
  const theme = useTheme();
  const { data: agent, isLoading } = useGetAgent();
  const [screenPosition, setScreenPosition] = useState<'left' | 'center' | 'right'>('center');
  const [open, setOpen] = useState(false);

  const handleTooltipClose = () => {
    setOpen(false);
  };

  const handleTooltipOpen = () => {
    setOpen(true);
    handleCopy();
  };

  useEffect(() => {
    setScreenPosition(agent?.web_embed_configuration?.screen_position ?? 'center');
  }, [agent]);

  const handleScreenPositionChange = (type: 'left' | 'center' | 'right') => {
    setScreenPosition(type);
  };

  const { handleSubmit, control, watch } = useForm<any>({
    values: {
      agent: {
        embed_url: agent?.embed_url || '',
        web_embed_configuration: agent?.web_embed_configuration || {}
      }
    }
  });

  const { mutateAsync: updateAgent, isPending } = useUpdateAgentConfig();

  const embedScript = `<script
  src="${WEB_EMBED_SCRIPT}"
  id="embed-script"
  onload="VoiceBot.open({
      agentToken: '${agent?.agent_token}'
    })">
</script>`;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(embedScript);
  };

  const onSubmit = (data: any) => {
    data.agent.web_embed_configuration.screen_position = screenPosition;
    updateAgent(data);
  };

  if (isLoading) {
    return <DeployWebLoader />;
  }

  return (
    <Box component='form' onSubmit={handleSubmit(onSubmit)}>
      <Typography variant='h6' mb={2} sx={{ fontSize: { xs: '1.125rem', lg: '1.25rem' } }}>
        Deploy Web
      </Typography>
      <Box
        bgcolor={theme.palette.background.paper}
        padding={{ xs: 2, lg: 3 }}
        borderRadius={2}
        border='1px solid #ececec'
      >
        <Typography variant='h6' sx={{ fontSize: { xs: '1.125rem', lg: '1.25rem' } }}>
          Deploy agent to website
        </Typography>
        <Typography color='secondary' mt={0.5} mb={3} sx={{ fontSize: { xs: 14, lg: 16 } }}>
          Insert this script into your &lt;head&gt; tag of your page.
        </Typography>
        <Input
          multiline
          minRows={4}
          name='agent.web_embed_configuration.embed_code'
          label='EMBED CODE'
          value={embedScript}
          fullWidth
          disabled
          control={control}
        />
        <ClickAwayListener onClickAway={handleTooltipClose}>
          <Tooltip
            PopperProps={{
              disablePortal: true
            }}
            onClose={handleTooltipClose}
            open={open}
            disableFocusListener
            disableHoverListener
            disableTouchListener
            placement='top'
            title='Copied'
          >
            <Button
              onClick={handleTooltipOpen}
              sx={{
                backgroundColor: theme.palette.primary.light,
                border: 'none',
                marginTop: 3,
                color: theme.palette.primary.main,
                width: { xs: '100%', sm: 'auto' }
              }}
            >
              Copy Code
            </Button>
          </Tooltip>
        </ClickAwayListener>
      </Box>
      <Box
        bgcolor={theme.palette.background.paper}
        padding={{ xs: 2, lg: 3 }}
        borderRadius={2}
        border='1px solid #ececec'
        marginTop={3}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <BrushIcon />
          <Typography variant='h6' fontSize='500'>
            Customize Styling
          </Typography>
        </Box>
        <Grid container spacing={3} mb={3} mt={1.5}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Input
              name='agent.web_embed_configuration.button_bg_color'
              label='Button Color'
              type='color'
              fullWidth
              control={control}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Input
              name='agent.web_embed_configuration.button_text_color'
              label='Icon Color'
              type='color'
              fullWidth
              control={control}
            />
          </Grid>
        </Grid>
        <Typography variant='h6' sx={{ fontSize: { xs: '1.125rem', lg: '1.25rem' } }}>
          Embed screen position
        </Typography>
        <Typography mt={0.5} mb={3} color='secondary' sx={{ fontSize: { xs: 14, lg: 16 } }}>
          Adjust the positioning of where the embed renders.
        </Typography>
        <Grid container spacing={1.5}>
          <Grid size={{ xs: 12, sm: 4, md: 4 }}>
            <Box
              onClick={() => handleScreenPositionChange('left')}
              sx={{
                cursor: 'pointer'
              }}
              borderRadius={3}
            >
              <EmbedWebLeftIcon
                isSelected={screenPosition === 'left'}
                buttonBgColor={watch('agent.web_embed_configuration.button_bg_color')}
                buttonColor={watch('agent.web_embed_configuration.button_text_color')}
              />
              <Typography
                color={screenPosition === 'left' ? 'primary' : 'secondary'}
                textAlign='center'
                marginTop={0.5}
              >
                Bottom left
              </Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 4, md: 4 }}>
            <Box
              onClick={() => handleScreenPositionChange('center')}
              sx={{
                cursor: 'pointer'
              }}
              borderRadius={3}
            >
              <EmbedWebCenterIcon
                isSelected={screenPosition === 'center'}
                buttonBgColor={watch('agent.web_embed_configuration.button_bg_color')}
                buttonColor={watch('agent.web_embed_configuration.button_text_color')}
              />
              <Typography
                color={screenPosition === 'center' ? 'primary' : 'secondary'}
                textAlign='center'
                marginTop={0.5}
              >
                Bottom Center
              </Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 4, md: 4 }}>
            <Box
              onClick={() => handleScreenPositionChange('right')}
              sx={{
                cursor: 'pointer'
              }}
              borderRadius={3}
            >
              <EmbedWebRightIcon
                isSelected={screenPosition === 'right'}
                buttonBgColor={watch('agent.web_embed_configuration.button_bg_color')}
                buttonColor={watch('agent.web_embed_configuration.button_text_color')}
              />
              <Typography
                color={screenPosition === 'right' ? 'primary' : 'secondary'}
                textAlign='center'
                marginTop={0.5}
              >
                Bottom Right
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Grid container spacing={2.5} marginTop={3}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Input
              name='agent.web_embed_configuration.screen_bottom_spacing'
              label='Bottom Spacing'
              control={control}
              slotProps={{
                input: {
                  endAdornment: <InputAdornment position='end'>px</InputAdornment>
                }
              }}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Input
              name='agent.web_embed_configuration.screen_side_spacing'
              label='Side Spacing'
              control={control}
              slotProps={{
                input: {
                  endAdornment: <InputAdornment position='end'>px</InputAdornment>
                }
              }}
              fullWidth
            />
          </Grid>
        </Grid>
      </Box>

      <Box
        bgcolor={theme.palette.background.paper}
        padding={{ xs: 2, lg: 3 }}
        borderRadius={2}
        border='1px solid #ececec'
        marginTop={3}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <EyeIcon />
          <Typography variant='h6' fontWeight='500' sx={{ fontSize: { xs: '1.125rem', lg: '1.25rem' } }}>
            Preview Your Embed
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            marginTop: 2,
            gap: 3,
            flexDirection: { xs: 'column', md: 'row' }
          }}
        >
          <Box width='100%'>
            <Input name='agent.embed_url' label='Embed Preview' control={control} fullWidth />
            <Typography
              fontSize='12px'
              color='secondary'
              marginTop={1}
              lineHeight={1.5}
              sx={{ fontSize: { xs: 11, lg: 12 } }}
            >
              Preview how your assistant will appear on your website (if iframe embedding is allowed). Note: Some
              websites may restrict iframe usage.
            </Typography>
          </Box>
          <Link
            target='_blank'
            rel='noopener noreferrer'
            style={{
              pointerEvents: !watch('agent.embed_url') ? 'none' : 'auto'
            }}
            to={`${urls.publicRoutes.embedPreview}?website=${watch('agent.embed_url')}&agentToken=${agent?.agent_token}&test=${false}`}
          >
            <Button sx={{ width: { xs: '100%', md: 'auto' }, mt: { xs: 1.5, md: 0 } }}>Preview</Button>
          </Link>
        </Box>
      </Box>
      <Box
        sx={{ display: 'flex', gap: '8px', justifyContent: { xs: 'space-between', sm: 'end' }, flexWrap: 'wrap' }}
        marginTop={3}
      >
        <Button
          sx={{
            borderColor: '#F3F3F3',
            background: 'white',
            width: { xs: '48%', sm: 'auto' }
          }}
          color='secondary'
          variant='outlined'
        >
          Cancel
        </Button>
        <Button type='submit' isPending={isPending} sx={{ width: { xs: '48%', sm: 'auto' } }}>
          Save
        </Button>
      </Box>
    </Box>
  );
}
